import type { MetricId }         from '../../../modules/dt-st/dashboard/types'
import type { MetricDescriptor } from '../types'

const QUEUE_METRICS:   MetricDescriptor['supportedAggregations'] = ['sum', 'count', 'average']
const RATE_METRICS:    MetricDescriptor['supportedAggregations'] = ['average', 'rate']
const ALL_COMPARISONS: MetricDescriptor['supportedComparisons']  = [
  'today_vs_yesterday', 'this_week_vs_last_week', 'this_month_vs_last_month',
]

export const METRIC_REGISTRY: Record<MetricId, MetricDescriptor> = {
  // ── Sales ───────────────────────────────────────────────────────────────────
  pending: {
    id: 'pending', category: 'queue',
    description:             'Orders waiting to be assigned to a sales agent',
    source:                  'sales',
    allowedWorkflows:        ['sales'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  assigned: {
    id: 'assigned', category: 'queue',
    description:             'Orders currently assigned but not yet in progress',
    source:                  'sales',
    allowedWorkflows:        ['sales'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  ready: {
    id: 'ready', category: 'session',
    description:             'Orders confirmed ready for shipping today',
    source:                  'sales',
    allowedWorkflows:        ['sales'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  upsellOrders: {
    id: 'upsellOrders', category: 'session',
    description:             'Upsell orders created from the sales queue today',
    source:                  'sales',
    allowedWorkflows:        ['sales'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  upsellRevenue: {
    id: 'upsellRevenue', category: 'performance',
    description:             'Revenue generated from upsell orders today',
    source:                  'sales',
    allowedWorkflows:        ['sales'],
    supportedAggregations:   ['sum', 'average'],
    supportedComparisons:    ALL_COMPARISONS,
  },
  noAnswer: {
    id: 'noAnswer', category: 'session',
    description:             'Orders closed with no-answer outcome today',
    source:                  'sales',
    allowedWorkflows:        ['sales'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  notInterested: {
    id: 'notInterested', category: 'session',
    description:             'Orders closed as not interested today',
    source:                  'sales',
    allowedWorkflows:        ['sales'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  conversionRate: {
    id: 'conversionRate', category: 'performance',
    description:             'Percentage of assigned orders converted to sales',
    source:                  'sales',
    allowedWorkflows:        ['sales'],
    supportedAggregations:   RATE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },

  // ── Platform / live operational ─────────────────────────────────────────────
  workingAgents: {
    id: 'workingAgents', category: 'team',
    description:             'Agents currently working on at least one order',
    source:                  'platform',
    allowedWorkflows:        ['sales', 'confirmation', 'shipping_confirmation', 'customer_service'],
    supportedAggregations:   ['count'],
    supportedComparisons:    [],
  },
  idleAgents: {
    id: 'idleAgents', category: 'team',
    description:             'Agents logged in but not working on any order',
    source:                  'platform',
    allowedWorkflows:        ['sales', 'confirmation', 'shipping_confirmation', 'customer_service'],
    supportedAggregations:   ['count'],
    supportedComparisons:    [],
  },
  postponed: {
    id: 'postponed', category: 'queue',
    description:             'Orders currently in postponed state',
    source:                  'sales',
    allowedWorkflows:        ['sales'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  oldestPending: {
    id: 'oldestPending', category: 'queue',
    description:             'How long the oldest unassigned order has been waiting (hours)',
    source:                  'platform',
    allowedWorkflows:        ['sales', 'confirmation', 'shipping_confirmation'],
    supportedAggregations:   ['max'],
    supportedComparisons:    [],
  },
  avgWaitTime: {
    id: 'avgWaitTime', category: 'performance',
    description:             'Average time orders wait before being assigned (minutes)',
    source:                  'platform',
    allowedWorkflows:        ['sales', 'confirmation', 'shipping_confirmation'],
    supportedAggregations:   ['average', 'min', 'max'],
    supportedComparisons:    ALL_COMPARISONS,
  },

  // ── Customer Service ────────────────────────────────────────────────────────
  pendingCases: {
    id: 'pendingCases', category: 'queue',
    description:             'Escalation cases waiting to be assigned',
    source:                  'customer_service',
    allowedWorkflows:        ['customer_service'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  openCases: {
    id: 'openCases', category: 'queue',
    description:             'Escalation cases currently in progress',
    source:                  'customer_service',
    allowedWorkflows:        ['customer_service'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  resolvedToday: {
    id: 'resolvedToday', category: 'session',
    description:             'Cases fully resolved today',
    source:                  'customer_service',
    allowedWorkflows:        ['customer_service'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  returnedToday: {
    id: 'returnedToday', category: 'session',
    description:             'Cases returned to origin workflow today',
    source:                  'customer_service',
    allowedWorkflows:        ['customer_service'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  avgResolutionTime: {
    id: 'avgResolutionTime', category: 'performance',
    description:             'Average time from case opened to resolved (minutes)',
    source:                  'customer_service',
    allowedWorkflows:        ['customer_service'],
    supportedAggregations:   ['average', 'min', 'max'],
    supportedComparisons:    ALL_COMPARISONS,
  },
  criticalCases: {
    id: 'criticalCases', category: 'escalation',
    description:             'Cases flagged with critical priority',
    source:                  'customer_service',
    allowedWorkflows:        ['customer_service'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },

  // ── Confirmation (DT-CF) ────────────────────────────────────────────────────
  cfPending: {
    id: 'cfPending', category: 'queue',
    description:             'Orders waiting to be assigned to a confirmation agent',
    source:                  'confirmation',
    allowedWorkflows:        ['confirmation'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  cfAssigned: {
    id: 'cfAssigned', category: 'queue',
    description:             'Orders assigned to a confirmation agent',
    source:                  'confirmation',
    allowedWorkflows:        ['confirmation'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  cfConfirmedToday: {
    id: 'cfConfirmedToday', category: 'session',
    description:             'Orders confirmed by agents today',
    source:                  'confirmation',
    allowedWorkflows:        ['confirmation'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  cfNoAnswerToday: {
    id: 'cfNoAnswerToday', category: 'session',
    description:             'Orders closed with no-answer today',
    source:                  'confirmation',
    allowedWorkflows:        ['confirmation'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  cfPostponed: {
    id: 'cfPostponed', category: 'queue',
    description:             'Orders currently in postponed state',
    source:                  'confirmation',
    allowedWorkflows:        ['confirmation'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  cfConversionRate: {
    id: 'cfConversionRate', category: 'performance',
    description:             'Percentage of confirmation sessions ending in confirmed outcome',
    source:                  'confirmation',
    allowedWorkflows:        ['confirmation'],
    supportedAggregations:   RATE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  cfAvgConfirmTime: {
    id: 'cfAvgConfirmTime', category: 'performance',
    description:             'Average duration of a confirmation session (minutes)',
    source:                  'confirmation',
    allowedWorkflows:        ['confirmation'],
    supportedAggregations:   ['average', 'min', 'max'],
    supportedComparisons:    ALL_COMPARISONS,
  },

  // ── Shipping Confirmation (DT-SC) ───────────────────────────────────────────
  scPending: {
    id: 'scPending', category: 'queue',
    description:             'Orders waiting to be assigned to a shipping confirmation agent',
    source:                  'shipping_confirmation',
    allowedWorkflows:        ['shipping_confirmation'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  scAssigned: {
    id: 'scAssigned', category: 'queue',
    description:             'Orders assigned to a shipping confirmation agent',
    source:                  'shipping_confirmation',
    allowedWorkflows:        ['shipping_confirmation'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  scConfirmedToday: {
    id: 'scConfirmedToday', category: 'session',
    description:             'Orders confirmed for shipping today',
    source:                  'shipping_confirmation',
    allowedWorkflows:        ['shipping_confirmation'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  scNoAnswerToday: {
    id: 'scNoAnswerToday', category: 'session',
    description:             'Orders closed with no-answer today',
    source:                  'shipping_confirmation',
    allowedWorkflows:        ['shipping_confirmation'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  scPostponed: {
    id: 'scPostponed', category: 'queue',
    description:             'Orders currently in postponed state',
    source:                  'shipping_confirmation',
    allowedWorkflows:        ['shipping_confirmation'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  scConversionRate: {
    id: 'scConversionRate', category: 'performance',
    description:             'Percentage of sessions confirmed for shipping',
    source:                  'shipping_confirmation',
    allowedWorkflows:        ['shipping_confirmation'],
    supportedAggregations:   RATE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  scChangesRequested: {
    id: 'scChangesRequested', category: 'session',
    description:             'Orders with customer-requested changes today',
    source:                  'shipping_confirmation',
    allowedWorkflows:        ['shipping_confirmation'],
    supportedAggregations:   QUEUE_METRICS,
    supportedComparisons:    ALL_COMPARISONS,
  },
  scAvgConfirmTime: {
    id: 'scAvgConfirmTime', category: 'performance',
    description:             'Average duration of a shipping confirmation session (minutes)',
    source:                  'shipping_confirmation',
    allowedWorkflows:        ['shipping_confirmation'],
    supportedAggregations:   ['average', 'min', 'max'],
    supportedComparisons:    ALL_COMPARISONS,
  },
}

export const METRIC_REGISTRY_LIST = Object.values(METRIC_REGISTRY)

export function getMetricsByWorkflow(workflowId: MetricDescriptor['source']): MetricDescriptor[] {
  return METRIC_REGISTRY_LIST.filter(
    (m) => m.source === workflowId || m.allowedWorkflows.includes(workflowId as never),
  )
}

export function getMetricsByCategory(category: MetricDescriptor['category']): MetricDescriptor[] {
  return METRIC_REGISTRY_LIST.filter((m) => m.category === category)
}
